import ManagedTable, { ManagedColumn } from '../../common/table/ManagedTable';
import { ScenarioAttributesDto, ScenarioTvMatchingKind } from '../../model/CORE';
import Utils from '../../common/util/Utils';
import { ProvidesKey } from '../../model/client/CORE';
import React, { CSSProperties } from 'react';
import { TableColumnTooltip } from '../../common/table/TableColumnTooltipComponent';
import { Flex } from '../common/Flex';

const TEXT_CENTER_STYLE: CSSProperties = { textAlign: 'center' };

export class ScenarioAttributesColumn {
    public static MULTIPLE_TV_MATCHING_LABEL = 'MUL';

    private static columnKindTooltip: TableColumnTooltip = {
        title: 'Matching Kind',
        content: (
            <Flex direction={'vertical'}>
                <Flex direction={'horizontal'}>
                    <Flex>{this.getMatchingKindLabel(ScenarioTvMatchingKind.SAME_TRAFFIC_VOLUME)}:</Flex>
                    <Flex>{this.getMatchingKindFullName(ScenarioTvMatchingKind.SAME_TRAFFIC_VOLUME)}</Flex>
                </Flex>
                <Flex direction={'horizontal'}>
                    <Flex>{this.getMatchingKindLabel(ScenarioTvMatchingKind.SAME_REFERENCE_LOCATION)}:</Flex>
                    <Flex>{this.getMatchingKindFullName(ScenarioTvMatchingKind.SAME_REFERENCE_LOCATION)}</Flex>
                </Flex>
                <Flex direction={'horizontal'}>
                    <Flex>{this.getMatchingKindLabel(ScenarioTvMatchingKind.OVERLAPPING_REFERENCE_LOCATION)}:</Flex>
                    <Flex>{this.getMatchingKindFullName(ScenarioTvMatchingKind.OVERLAPPING_REFERENCE_LOCATION)}</Flex>
                </Flex>
                <Flex direction={'horizontal'}>
                    <Flex>{this.getMatchingKindLabel(ScenarioTvMatchingKind.INDIRECT_OFFLOAD)}:</Flex>
                    <Flex>{this.getMatchingKindFullName(ScenarioTvMatchingKind.INDIRECT_OFFLOAD)}</Flex>
                </Flex>
                <Flex direction={'horizontal'}>
                    <Flex>{this.MULTIPLE_TV_MATCHING_LABEL}:</Flex>
                    <Flex>Multiple Matching Kind</Flex>
                </Flex>
            </Flex>
        ),
    };

    private static columnRestrTooltip: TableColumnTooltip = {
        title: 'Restriction',
        content: (
            <Flex direction={'vertical'}>
                <Flex direction={'horizontal'}>Disabled/Ignored RAD restrictions apply</Flex>
            </Flex>
        ),
    };

    private static columCommonTooltip: TableColumnTooltip = {
        title: 'Common',
        content: (
            <Flex direction={'vertical'}>
                <Flex direction={'horizontal'}>Flights captured in common between the TV and the Scenario</Flex>
            </Flex>
        ),
    };

    private static columOtherTooltip: TableColumnTooltip = {
        title: 'Others',
        content: (
            <Flex direction={'vertical'}>
                <Flex direction={'horizontal'}>Flights captured by the scenario but not the TV</Flex>
            </Flex>
        ),
    };

    private static columFlightsTooltip: TableColumnTooltip = {
        title: 'Flights',
        content: (
            <Flex direction={'vertical'}>
                <Flex direction={'horizontal'}>Flights captured by the scenario</Flex>
            </Flex>
        ),
    };

    public static readonly PUBLICATION_ID = new ManagedColumn<ScenarioAttributesDto>(
        'ID',
        '6em',
        obj => obj.publicationId,
    );
    public static readonly RESTRICTED = new ManagedColumn<ScenarioAttributesDto>(
        'Restr.',
        '4em',
        obj =>
            (obj.disabledRestrictionIds != null && obj.disabledRestrictionIds.length > 0) ||
            (obj.ignoredRestrictionIds != null && obj.ignoredRestrictionIds.length > 0)
                ? 'X'
                : '',
        { cellStyle: () => TEXT_CENTER_STYLE, tooltip: this.columnRestrTooltip },
    );
    public static readonly OFFLOAD = new ManagedColumn<ScenarioAttributesDto>('OFFLOAD', '12em', obj =>
        Utils.concat('\n', obj.offLoadAirspaceIds),
    );
    public static readonly ONLOAD = new ManagedColumn<ScenarioAttributesDto>('ONLOAD', '35em', obj =>
        Utils.concat('\n', obj.onLoadAirspaceIds),
    );
    public static readonly FROM = new ManagedColumn<ScenarioAttributesDto>('FROM', '15em', obj => obj.from);
    public static readonly TO = new ManagedColumn<ScenarioAttributesDto>('TO', '15em', obj => obj.to);
    public static readonly COMMENTS = new ManagedColumn<ScenarioAttributesDto>('COMMENTS', '10em', obj => obj.comments);
    public static readonly MATCHING_KIND = new ManagedColumn<ScenarioAttributesDto>(
        'Kind',
        '5em',
        obj => ScenarioAttributesColumn.getMatchingKind(obj),
        { tooltip: this.columnKindTooltip },
    );
    public static readonly TV_CAPTURED_FLIGHTS = new ManagedColumn<ScenarioAttributesDto>(
        'Common',
        '7em',
        obj => (obj.problematicCapturedFlights ? obj.problematicCapturedFlights.length : 0),
        { cellStyle: () => TEXT_CENTER_STYLE, tooltip: this.columCommonTooltip },
    );
    public static readonly OTHER_CAPTURED_FLIGHTS = new ManagedColumn<ScenarioAttributesDto>(
        'Others',
        '5em',
        obj =>
            obj.problematicCapturedFlights && obj.scenarioCapturedFlights
                ? obj.scenarioCapturedFlights -
                      (obj.problematicCapturedFlights ? obj.problematicCapturedFlights.length : 0) >=
                  0
                    ? obj.scenarioCapturedFlights -
                      (obj.problematicCapturedFlights ? obj.problematicCapturedFlights.length : 0)
                    : 0
                : '/',
        { cellStyle: () => TEXT_CENTER_STYLE, tooltip: this.columOtherTooltip },
    );
    public static readonly SCENARIO_CAPTURED_FLIGHTS = new ManagedColumn<ScenarioAttributesDto>(
        'Flights',
        '6em',
        obj => (obj.scenarioCapturedFlights ? obj.scenarioCapturedFlights : 0),
        { cellStyle: () => TEXT_CENTER_STYLE, tooltip: this.columFlightsTooltip },
    );

    public static getMatchingKind(scenario: ScenarioAttributesDto): string {
        if (Object.keys(scenario.scenarioTvCaptureDetailsByTv ?? {}).length === 1) {
            return this.getMatchingKindLabel(
                Object.values(scenario.scenarioTvCaptureDetailsByTv ?? [])[0].matchingKind,
            );
        } else {
            if (Object.keys(scenario.scenarioTvCaptureDetailsByTv ?? {}).length === 1) {
                console.error('No element returned for scenarioTvCaptureDetailsByTv');
            } else {
                return this.MULTIPLE_TV_MATCHING_LABEL;
            }
        }
    }

    public static getMatchingKindLabel(matchingKind: ScenarioTvMatchingKind): string {
        switch (matchingKind) {
            case ScenarioTvMatchingKind.INDIRECT_OFFLOAD:
                return 'IND';
            case ScenarioTvMatchingKind.OVERLAPPING_REFERENCE_LOCATION:
                return 'OVR';
            case ScenarioTvMatchingKind.SAME_REFERENCE_LOCATION:
                return 'SRL';
            case ScenarioTvMatchingKind.SAME_TRAFFIC_VOLUME:
                return 'STV';
        }
    }

    private static getMatchingKindFullName(matchingKind: ScenarioTvMatchingKind): string {
        switch (matchingKind) {
            case ScenarioTvMatchingKind.INDIRECT_OFFLOAD:
                return 'Indirect Offload';
            case ScenarioTvMatchingKind.OVERLAPPING_REFERENCE_LOCATION:
                return 'Overlapping Reference Location';
            case ScenarioTvMatchingKind.SAME_REFERENCE_LOCATION:
                return 'Same reference Location';
            case ScenarioTvMatchingKind.SAME_TRAFFIC_VOLUME:
                return 'Same Traffic Volume';
        }
    }
}

export const SCENARIO_ATTRIBUTES_COLUMNS = [
    ScenarioAttributesColumn.PUBLICATION_ID,
    ScenarioAttributesColumn.OFFLOAD,
    ScenarioAttributesColumn.ONLOAD,
    ScenarioAttributesColumn.FROM,
    ScenarioAttributesColumn.TO,
    ScenarioAttributesColumn.COMMENTS,
];

export const SCENARIO_ATTRIBUTES_SIMPLIFY_COLUMNS = [
    ScenarioAttributesColumn.PUBLICATION_ID,
    ScenarioAttributesColumn.RESTRICTED,
];

export const SCENARIO_ATTRIBUTES_KEY_PROVIDER = new ProvidesKey<ScenarioAttributesDto>(item => item.scenarioId);
export class ScenarionAtributesTable extends ManagedTable<ScenarioAttributesDto> {}
